export type GeoServerLayerId = {
  id: string;
  namespace: string;
  namespaceURI: string;
};

export type GeoServerNamespace = {
  namespace: string;
  namespaceURI: string;
};

export type WFSFilterParams = {
  attributeName: string;
  value: string;
};

export interface WPSEntity {
  toWPS(): string;
}

export class WPSComplexDataInput implements WPSEntity {
  constructor(
    private identifier: string,
    private value: string,
    private mimeType: string
  ) {}

  toWPS() {
    return `
      <wps:Input>
        <ows:Identifier>${this.identifier}</ows:Identifier>
        <wps:Data>
          <wps:ComplexData mimeType="${this.mimeType}"><![CDATA[${this.value}]]></wps:ComplexData>
        </wps:Data>
      </wps:Input>
    `;
  }
}

export class WPSLateralInput implements WPSEntity {
  constructor(
    private identifier: string,
    private value: string
  ) {}

  toWPS() {
    return `
      <wps:Input>
        <ows:Identifier>${this.identifier}</ows:Identifier>
        <wps:Data>
          <wps:LiteralData>${this.value}</wps:LiteralData>
        </wps:Data>
      </wps:Input>
    `;
  }
}

export class WPSLayerInput implements WPSEntity {
  constructor(
    private identifier: string,
    private layerId: GeoServerLayerId,
    private filter?: WFSFilterParams
  ) {}

  toWPS() {
    if (this.filter) {
      return new WPSSubprocessInput(
        'features',
        'gs:Query',
        'text/xml; subtype=wfs-collection/1.0',
        [
          new WPSComplexDataInput(
            'filter',
            `${this.filter.attributeName} = '${this.filter.value}'`,
            'text/plain; subtype=cql'
          ),
          new WPSLayerInput('features', this.layerId),
        ]
      ).toWPS();
    }

    return `
      <wps:Input>
        <ows:Identifier>${this.identifier}</ows:Identifier>
        <wps:Reference mimeType="text/xml" xlink:href="http://geoserver/wfs" method="POST">
          <wps:Body>
            <wfs:GetFeature
              service="WFS"
              version="1.0.0"
              outputFormat="GML2"
              xmlns:${this.layerId.namespace}="${this.layerId.namespaceURI}"
            >
              <wfs:Query typeName="${this.layerId.namespace}:${this.layerId.id}"/>
            </wfs:GetFeature>
          </wps:Body>
        </wps:Reference>
      </wps:Input>
    `;
  }
}

export class WPSSubprocessInput implements WPSEntity {
  constructor(
    private identifier: string,
    private executeIdentifier: string,
    private outputMime: string,
    private inputs: WPSEntity[] = []
  ) {}

  toWPS() {
    return `
      <wps:Input>
        <ows:Identifier>${this.identifier}</ows:Identifier>
        <wps:Reference mimeType="text/xml; subtype=gml/3.1.1" xlink:href="http://geoserver/wps" method="POST">
          <wps:Body>
            ${new WPSExecute(this.executeIdentifier, this.outputMime, this.inputs).toWPS()}
          </wps:Body>
        </wps:Reference>
      </wps:Input>
    `;
  }
}

export class WPSExecute implements WPSEntity {
  constructor(
    private identifier: string,
    private outputMime: string,
    private inputs: WPSEntity[] = []
  ) {}

  toWPS(additionalNamespaces: GeoServerNamespace[] = [], addNamespace: boolean = false) {
    const namespacesStr = additionalNamespaces
      .map((ns) => `xmlns:${ns.namespace}="${ns.namespaceURI}"`)
      .join(' ');
    const defaultNamespace = addNamespace ? 'xmlns="http://www.opengis.net/wps/1.0.0"' : '';
    return `
      <wps:Execute version="1.0.0" service="WPS" ${defaultNamespace} ${namespacesStr}>
        <ows:Identifier>${this.identifier}</ows:Identifier>
        <wps:DataInputs>
          ${this.inputs.map((input) => input.toWPS()).join('')}
        </wps:DataInputs>
        <wps:ResponseForm>
          <wps:RawDataOutput mimeType="${this.outputMime}">
            <ows:Identifier>result</ows:Identifier>
          </wps:RawDataOutput>
        </wps:ResponseForm>
      </wps:Execute>
    `;
  }
}

export const MAIN_NAMESPACES = [
  { namespace: 'xsi', namespaceURI: 'http://www.w3.org/2001/XMLSchema-instance' },
  { namespace: 'wfs', namespaceURI: 'http://www.opengis.net/wfs' },
  { namespace: 'wps', namespaceURI: 'http://www.opengis.net/wps/1.0.0' },
  { namespace: 'ows', namespaceURI: 'http://www.opengis.net/ows/1.1' },
  { namespace: 'gml', namespaceURI: 'http://www.opengis.net/gml' },
  { namespace: 'ogc', namespaceURI: 'http://www.opengis.net/ogc' },
  { namespace: 'wcs', namespaceURI: 'http://www.opengis.net/wcs/1.1.1' },
  { namespace: 'fes', namespaceURI: 'http://www.opengis.net/fes/2.0' },
  { namespace: 'xlink', namespaceURI: 'http://www.w3.org/1999/xlink' },
];
